/**
 * WindView（大会公式のパイバル情報サイト）の画面をスクリーンショット→AIに
 * JSON化してもらったものを読み込むための、DOM非依存の変換ロジック。
 *
 * WindView自体の内部APIには直接アクセスしない（docs/09参照:
 * 利用者のiPhone Safariではdevtools調査が難航し実データ通信を特定できな
 * かった。他サイトの非公開APIへ推測でアクセスするのは競技結果に関わる
 * 数字として危険、という判断）。代わりにtask-circles.tsのJSON一括読み込み
 * と同じ「写真をAIに見せてJSON化→貼り付け」方式を踏襲する。
 */

import { magneticToTrueHeading, type SourceKind, type WindSample } from '@gv4d/core';

/** JSONインポート1件ぶんの、想定するキー（すべて緩く受ける。無くてもエラーにしない） */
export interface WindviewImportEntry {
  altM?: unknown;
  dirMagDeg?: unknown;
  speedMps?: unknown;
}

export interface WindviewImportResult {
  samples: WindSample[];
  /** 読み取れなかった項目（1件の異常で全体を止めないよう、続行しつつ積む） */
  skipped: Array<{ index: number; reason: string }>;
}

const SOURCE: SourceKind = 'official';

/**
 * WindViewのJSON配列をWindSample[]へ変換する。
 *
 * - 高度: WindViewの表はAGL（地上高度）。アプリ内の他の高度はMSL基準
 *   なので `altMsl = altM + groundElevationM` に変換する（標高の自動取得
 *   源が無いため、呼び出し側が利用者入力の地上標高を渡す）
 * - 風向: WindViewの`To (Mag)`は磁方位。WindSample.dirは真方位の規約
 *   なので`magneticToTrueHeading`で変換する
 * - 個々のサンプルにタイムスタンプが無いため`t`は読み込み時刻で埋める
 *   （windSource: 'windview'側は時間帯びん分けを経由せず高度順に並べる
 *   だけなので実質使われない）
 */
export function parseWindviewImport(
  json: unknown,
  ref: { lat: number; lon: number },
  groundElevationM: number,
  declinationDeg: number,
): WindviewImportResult {
  const samples: WindSample[] = [];
  const skipped: Array<{ index: number; reason: string }> = [];
  if (!Array.isArray(json)) {
    return { samples, skipped: [{ index: -1, reason: 'JSONの内容が配列ではありません' }] };
  }
  const now = Date.now();
  json.forEach((raw, index) => {
    const e = raw as WindviewImportEntry | null;
    const altM = typeof e?.altM === 'number' && Number.isFinite(e.altM) ? e.altM : null;
    if (altM === null) {
      skipped.push({ index, reason: '高度(altM)がありません' });
      return;
    }
    const dirMagDeg =
      typeof e?.dirMagDeg === 'number' && Number.isFinite(e.dirMagDeg) ? e.dirMagDeg : null;
    if (dirMagDeg === null) {
      skipped.push({ index, reason: '風向(dirMagDeg)がありません' });
      return;
    }
    const speedMps =
      typeof e?.speedMps === 'number' && Number.isFinite(e.speedMps) ? e.speedMps : null;
    if (speedMps === null) {
      skipped.push({ index, reason: '風速(speedMps)がありません' });
      return;
    }
    samples.push({
      lat: ref.lat,
      lon: ref.lon,
      alt: altM + groundElevationM,
      t: now,
      dir: magneticToTrueHeading(dirMagDeg, declinationDeg),
      speed: speedMps,
      source: SOURCE,
      quality: 1,
    });
  });
  return { samples, skipped };
}
